import { useEffect, useState } from "react";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import {
  getUserCounts,
  getClientCounts,
  getProjectCounts,
  getInvoiceCounts,
  getProjectsSnapshot,
} from "@/services/dashboardService";
import type { EntityBreakdown, ProjectSnapshotItem } from "@/services/dashboardService";

type DashboardStats = {
  users: EntityBreakdown | null;
  clients: EntityBreakdown | null;
  projects: EntityBreakdown | null;
  invoices: EntityBreakdown | null;
  projectsSnapshot: ProjectSnapshotItem[];
  loading: boolean;
};

export default function useDashboardStats(): DashboardStats {
  const { user } = useAuth();
  const { hasUsersAccess, hasClientsAccess, hasProjectsAccess, hasInvoicesAccess } = usePermissions();

  const [users, setUsers] = useState<EntityBreakdown | null>(null);
  const [clients, setClients] = useState<EntityBreakdown | null>(null);
  const [projects, setProjects] = useState<EntityBreakdown | null>(null);
  const [invoices, setInvoices] = useState<EntityBreakdown | null>(null);
  const [projectsSnapshot, setProjectsSnapshot] = useState<ProjectSnapshotItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    async function fetch() {
      const promises: Promise<void>[] = [];

      if (hasUsersAccess) {
        promises.push(
          getUserCounts().then((r) => {
            if (!cancelled) setUsers(r);
          }),
        );
      }
      if (hasClientsAccess) {
        promises.push(
          getClientCounts().then((r) => {
            if (!cancelled) setClients(r);
          }),
        );
      }
      if (hasProjectsAccess) {
        promises.push(
          getProjectCounts().then((r) => {
            if (!cancelled) setProjects(r);
          }),
        );
        promises.push(
          getProjectsSnapshot().then((r) => {
            if (!cancelled) setProjectsSnapshot(r);
          }),
        );
      }
      if (hasInvoicesAccess) {
        promises.push(
          getInvoiceCounts().then((r) => {
            if (!cancelled) setInvoices(r);
          }),
        );
      }

      await Promise.allSettled(promises);
      if (!cancelled) setLoading(false);
    }

    fetch();

    return () => {
      cancelled = true;
    };
  }, [user, hasUsersAccess, hasClientsAccess, hasProjectsAccess, hasInvoicesAccess]);

  return { users, clients, projects, invoices, projectsSnapshot, loading };
}
